// VisibilityTracker.js

import React, { useEffect, useRef } from 'react';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';

/**
 * Tracks when at least 50 % of the wrapped element becomes visible and
 * increments either `feedImpressionCount` or `discoveryBoxImpressionCount`
 * **once** for that post.
 *
 * @param {string}  postId          Firestore doc ID of the post
 * @param {boolean} isDiscoveryBox  true  → update discoveryBoxImpressionCount
 *                                  false → update feedImpressionCount
 * @param {string}  collectionName  Optional – Firestore collection
 *                                  Defaults to 'looksPosts'
 */
const VisibilityTracker = ({
  postId,
  children,
  isDiscoveryBox = false,
  collectionName = 'looksPosts',
}) => {
  const containerRef   = useRef(null);
  const observerRef    = useRef(null);
  const hasIncremented = useRef(false);

  useEffect(() => {
    if (!postId) return;

    const field = isDiscoveryBox
      ? 'discoveryBoxImpressionCount'
      : 'feedImpressionCount';

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasIncremented.current) {
          hasIncremented.current = true;

          // 🔄  Increment the chosen counter *once*
          const postRef = doc(db, collectionName, postId);
          updateDoc(postRef, { [field]: increment(1) }).catch((err) =>
            console.error(
              `VisibilityTracker: failed to increment ${field} for ${postId} in ${collectionName}`,
              err,
            ),
          );

          observerRef.current?.unobserve(containerRef.current);
        }
      },
      { threshold: 0.5 }, // 50 % visible
    );

    if (containerRef.current) {
      observerRef.current.observe(containerRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [postId, isDiscoveryBox, collectionName]);

  return <div ref={containerRef}>{children}</div>;
};

export default VisibilityTracker;